import { useState } from 'react';

import { ChevronDown, ChevronRight, Copy, Minus } from 'lucide-react';

import { useConfirmModal } from '@/hooks/useConfirmModal';
import { cn } from '@/lib/utils';

import CustomTable from './CustomTable';
import InlineEdit from './InlineEdit';
import MultiText from './MultiText';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import File from './ui/file';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const VariantField = ({
  fieldValue,
  handleFieldChange,
  id,
  mode = 'edit',
  placeholder,
  type,
}) => {
  const isEdit = mode !== 'static';

  if (type === 'date')
    return isEdit ? (
      <Input
        maxLength={10}
        onChange={({ target }) => handleFieldChange(id, target.value)}
        placeholder="dd/mm/yyyy"
        type="date"
        value={fieldValue}
      />
    ) : (
      fieldValue
    );

  if (type === 'file')
    return (
      <>
        {isEdit && (
          <File
            onChange={(file) => handleFieldChange(id, file)}
            value={fieldValue}
          />
        )}
        {fieldValue && (
          <img src={fieldValue?.base64 ? fieldValue.base64 : fieldValue} />
        )}
      </>
    );

  if (type === 'multi-text')
    return (
      <MultiText
        fieldValue={fieldValue}
        handleFieldChange={handleFieldChange}
        id={id}
        mode={mode}
        placeholder={placeholder}
      />
    );

  if (type === 'table')
    return (
      <CustomTable
        mode={mode}
        onChange={(value) => {
          handleFieldChange(id, value);
        }}
        value={fieldValue}
      />
    );

  if (type === 'textarea')
    return isEdit ? (
      <Textarea
        autoResize
        className="font-mono resize-none"
        onChange={({ target }) => handleFieldChange(id, target.value)}
        placeholder={placeholder || `Enter content for this section...`}
        value={fieldValue}
      />
    ) : (
      fieldValue
    );

  return isEdit ? (
    <Input
      className="min-w-80"
      onChange={({ target }) => handleFieldChange(id, target.value)}
      placeholder={placeholder || `Enter content for this section...`}
      type="text"
      value={fieldValue}
    />
  ) : (
    fieldValue
  );
};

const CardBlock = ({
  collapsible = true,
  fieldPlaceholder = '',
  formData,
  hideDetailTitle = false,
  id,
  mode = 'edit',
  sections,
  setFormData,
  setSections,
  titlePlaceholder = 'New Text Section',
  type,
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const { showConfirmModal } = useConfirmModal();

  const isEdit = mode !== 'static';
  const isExpanded = expandedSections[id] ?? true;
  const { title, value: fieldValue } = formData[id] || {};

  const handleSectionToggle = () => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: prev[id] === undefined ? false : !prev[id],
    }));
  };

  const handleTitleChange = (title) => {
    setFormData((prev) => ({
      ...prev,
      [id]: { title, value: prev[id]?.value },
    }));
  };

  const handleDuplicateClick = () => {
    const index = sections.findIndex((item) => item.id === id);
    const nextId = +new Date();

    setSections([
      ...sections.slice(0, index + 1),
      { ...sections[index], id: nextId },
      ...sections.slice(index + 1),
    ]);

    setFormData((prev) => ({
      ...prev,
      [nextId]: { ...formData[id] },
    }));
  };

  const handleDeleteClick = () => {
    showConfirmModal({
      confirmText: 'OK',
      description: `Are you sure you want to delete this section?`,
      onConfirm: () =>
        setSections([...sections.filter((item) => item.id !== id)]),
      title: 'Delete Section',
    });
  };

  const handleFieldChange = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      [id]: { title: prev[id]?.title, value },
    }));
  };

  return (
    <Card className="mb-4 relative" key={id}>
      <Collapsible onOpenChange={handleSectionToggle} defaultOpen={true}>
        <CardHeader
          className={cn(
            'pb-0 mb-6',
            hideDetailTitle && 'absolute right-0 top-0'
          )}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="grow text-base font-medium">
              {!hideDetailTitle &&
                (isEdit ? (
                  <InlineEdit
                    className="md:text-base"
                    onChange={handleTitleChange}
                    placeholder={titlePlaceholder}
                    value={title}
                  />
                ) : (
                  title
                ))}
            </CardTitle>
            <div>
              {isEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDuplicateClick}
                >
                  <Copy />
                </Button>
              )}
              {isEdit && (
                <Button variant="ghost" size="icon" onClick={handleDeleteClick}>
                  <Minus />
                </Button>
              )}
              {collapsible && (
                <CollapsibleTrigger>
                  <div className="flex items-center p-3">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                </CollapsibleTrigger>
              )}
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent className={cn('mt-6', hideDetailTitle && 'mr-24')}>
          <CardContent className="pt-0">
            <VariantField
              id={id}
              fieldValue={fieldValue}
              handleFieldChange={handleFieldChange}
              mode={mode}
              placeholder={fieldPlaceholder}
              type={type}
            />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default CardBlock;
